// LocalStorage-backed repository with logging
// Mirrors the shape/behaviour of InMemoryTradeRepository but persists to window.localStorage

import type { TradeRepository } from '@/domain/trade/interfaces/TradeRepository';
import { TradeFactory } from '@/domain/trade/factories/TradeFactory';
import { Trade } from '@/domain/trade/entities/Trade';

export type RepoTrade = {
  id: string;
  market: 'Crypto' | 'Forex' | 'All';
  symbol: string;
  entryDate: string;
  size: number;
  price: number;
  side: 'LONG' | 'SHORT';
  status: 'OPEN' | 'CLOSED' | 'FILLED';
  pnl: number;
  notes?: string;
  entry?: string;
  confluence?: { timeframe?: string; type: string }[];
  sl?: number;
  slIsBE?: boolean;
  tp1?: number;
  tp2?: number;
  tp3?: number;
  tp4?: number;
  tp1IsHit?: boolean;
  tp2IsHit?: boolean;
  tp3IsHit?: boolean;
  tp4IsHit?: boolean;
  margin?: number;
  leverage?: number;
  analysisId?: string; // optional link to analysis
  userId?: string;
};

const STORAGE_KEY = 'mt_trades_v1';

const DEFAULT_MOCK_TRADES: RepoTrade[] = [
  {
    id: 't1',
    market: 'Crypto',
    symbol: 'ETHUSD',
    entryDate: '2025-12-21T10:12:00Z',
    size: 0.51,
    price: 1800.5,
    side: 'SHORT',
    status: 'OPEN',
    pnl: 0,
    notes: 'Scalp-Short nach Fehlausbruch.',
    entry: '1802.0',
    sl: 1815.0,
    tp1: 1790.0,
    tp2: 1775.0,
    tp3: 1750.0,
    margin: 120,
    leverage: 10,
  },
  {
    id: 't2',
    market: 'Crypto',
    symbol: 'ETHUSD',
    entryDate: '2025-12-21T09:50:00Z',
    size: 0.37,
    price: 1795.3,
    side: 'LONG',
    status: 'OPEN',
    pnl: 0,
    notes: 'Rebound nach Support.',
    entry: '1794.0',
    sl: 1790.0,
    tp1: 1805.0,
    tp2: 1820.0,
    tp3: 1850.0,
    margin: 90,
    leverage: 8,
  },
  {
    id: 't3',
    market: 'Forex',
    symbol: 'EURUSD',
    entryDate: '2025-12-20T08:30:00Z',
    size: 10000,
    price: 1.1203,
    side: 'LONG',
    status: 'CLOSED',
    pnl: -12.5,
    notes: 'Reverted on news',
    entry: '1.1190',
    sl: 1.117,
    tp1: 1.125,
    tp2: 1.128,
    tp3: 1.13,
    margin: 100,
    leverage: 10,
  },
  {
    id: 't4',
    market: 'Crypto',
    symbol: 'BTCUSD',
    entryDate: '2025-12-22T11:00:00Z',
    size: 0.12,
    price: 42000.0,
    side: 'LONG',
    status: 'OPEN',
    pnl: 150.5,
    notes: 'Breakout trade, trailing SL.',
    entry: '41950',
    sl: 41700,
    tp1: 42500,
    tp2: 43000,
    tp3: 44000,
    margin: 500,
    leverage: 5,
  },
  {
    id: 't5',
    market: 'Crypto',
    symbol: 'SOLUSD',
    entryDate: '2025-12-22T10:30:00Z',
    size: 10,
    price: 95.2,
    side: 'SHORT',
    status: 'OPEN',
    pnl: -20.1,
    notes: 'Short nach Fehlausbruch.',
    entry: '96.0',
    sl: 98.0,
    tp1: 92.0,
    tp2: 89.0,
    tp3: 85.0,
    margin: 200,
    leverage: 3,
  },
  {
    id: 't6',
    market: 'Forex',
    symbol: 'USDJPY',
    entryDate: '2025-12-22T09:45:00Z',
    size: 5000,
    price: 134.55,
    side: 'LONG',
    status: 'OPEN',
    pnl: 10.0,
    notes: 'Range-Breakout, TP1 erreicht.',
    entry: '134.20',
    sl: 133.8,
    tp1: 134.8,
    tp2: 135.2,
    tp3: 136.0,
    margin: 150,
    leverage: 20,
  },
  {
    id: 't7',
    market: 'Crypto',
    symbol: 'ADAUSD',
    entryDate: '2025-12-22T08:10:00Z',
    size: 200,
    price: 1.25,
    side: 'LONG',
    status: 'OPEN',
    pnl: 5.5,
    notes: 'Scalp, schnelle Bewegung erwartet.',
    entry: '1.24',
    sl: 1.22,
    tp1: 1.28,
    tp2: 1.32,
    tp3: 1.36,
    margin: 50,
    leverage: 10,
  },
  {
    id: 't8',
    market: 'Forex',
    symbol: 'GBPUSD',
    entryDate: '2025-12-22T07:30:00Z',
    size: 8000,
    price: 1.32,
    side: 'SHORT',
    status: 'OPEN',
    pnl: -8.2,
    notes: 'News-Event, SL eng.',
    entry: '1.3210',
    sl: 1.323,
    tp1: 1.318,
    tp2: 1.315,
    tp3: 1.31,
    margin: 120,
    leverage: 15,
  },
  {
    id: 't9',
    market: 'Crypto',
    symbol: 'XRPUSD',
    entryDate: '2025-12-22T06:50:00Z',
    size: 500,
    price: 0.95,
    side: 'LONG',
    status: 'OPEN',
    pnl: 2.1,
    notes: 'TP1 fast erreicht.',
    entry: '0.94',
    sl: 0.92,
    tp1: 0.97,
    tp2: 1.0,
    tp3: 1.05,
    margin: 30,
    leverage: 8,
  },
  {
    id: 't10',
    market: 'Crypto',
    symbol: 'DOGEUSD',
    entryDate: '2025-12-22T06:20:00Z',
    size: 1000,
    price: 0.12,
    side: 'SHORT',
    status: 'OPEN',
    pnl: -1.5,
    notes: 'Memecoin-Short, hohes Risiko.',
    entry: '0.13',
    sl: 0.14,
    tp1: 0.11,
    tp2: 0.1,
    tp3: 0.09,
    margin: 25,
    leverage: 2,
  },
  {
    id: 't11',
    market: 'Forex',
    symbol: 'AUDUSD',
    entryDate: '2025-12-22T05:40:00Z',
    size: 6000,
    price: 0.755,
    side: 'LONG',
    status: 'OPEN',
    pnl: 3.3,
    notes: 'TP2 Ziel, SL nachgezogen.',
    entry: '0.7540',
    sl: 0.752,
    tp1: 0.757,
    tp2: 0.76,
    tp3: 0.765,
    margin: 80,
    leverage: 12,
  },
  {
    id: 't12',
    market: 'Crypto',
    symbol: 'MATICUSD',
    entryDate: '2025-12-22T05:10:00Z',
    size: 300,
    price: 2.1,
    side: 'LONG',
    status: 'OPEN',
    pnl: 7.7,
    notes: 'Layer2 Hype, TP1 erreicht.',
    entry: '2.05',
    sl: 2.0,
    tp1: 2.15,
    tp2: 2.25,
    tp3: 2.4,
    margin: 60,
    leverage: 7,
  },
  {
    id: 't13',
    market: 'Forex',
    symbol: 'USDCAD',
    entryDate: '2025-12-22T04:30:00Z',
    size: 4000,
    price: 1.27,
    side: 'SHORT',
    status: 'OPEN',
    pnl: -4.0,
    notes: 'Korrektur-Short, TP1 offen.',
    entry: '1.2710',
    sl: 1.273,
    tp1: 1.268,
    tp2: 1.265,
    tp3: 1.26,
    margin: 70,
    leverage: 9,
  },
  {
    id: 't14',
    market: 'Crypto',
    symbol: 'BTCUSD',
    entryDate: '2025-12-27T21:51:00Z',
    size: 1,
    price: 80000,
    side: 'LONG',
    status: 'OPEN',
    pnl: 0,
    notes: 'Test Entry',
    entry: '79900',
    sl: 78000,
    tp1: 81000,
    tp2: 82000,
    tp3: 83000,
    tp4: 84000,
    margin: 1000,
    leverage: 20,
  },
];

// Implement the domain TradeRepository interface: accept/return domain Trade entities
export class LocalStorageTradeRepository implements TradeRepository {
  private trades: RepoTrade[] = [];
  private key: string;

  // options: { seedDefaults?: boolean } - when false, do not seed DEFAULT_MOCK_TRADES on first run
  constructor(key = STORAGE_KEY, options?: { seedDefaults?: boolean }) {
    this.key = key;
    const seedDefaults = options?.seedDefaults !== undefined ? options?.seedDefaults : true;
    const raw = (() => {
      try {
        return window.localStorage.getItem(this.key);
      } catch {
        // eslint-disable-next-line no-console
        console.warn('[LocalStorageRepo] localStorage.getItem threw, ignoring storage');
        return null;
      }
    })();

    if (raw) {
      try {
        const parsed = JSON.parse(raw) as RepoTrade[];
        this.trades = parsed.map((t) => ({ ...t }));
        // eslint-disable-next-line no-console
        console.info('[LocalStorageRepo] loaded', this.trades.length, 'trades');
      } catch {
        // Malformed JSON in storage should not crash the app/tests; fall back to defaults/empty
        // eslint-disable-next-line no-console
        console.warn(
          '[LocalStorageRepo] could not parse stored trades, falling back to defaults/empty'
        );
        this.trades = seedDefaults ? DEFAULT_MOCK_TRADES.map((t) => ({ ...t })) : [];
        if (seedDefaults) this.flush();
      }
    } else if (seedDefaults) {
      this.trades = DEFAULT_MOCK_TRADES.map((t) => ({ ...t }));
      this.flush();
      // eslint-disable-next-line no-console
      console.info('[LocalStorageRepo] initialized with defaults');
    } else {
      this.trades = [];
      // eslint-disable-next-line no-console
      console.info('[LocalStorageRepo] initialized empty (no defaults)');
    }
  }

  // Public helper to seed repository with additional trades and persist immediately.
  // Accepts an array of RepoTrade and appends them to storage.
  seed(trades: RepoTrade[]) {
    if (!Array.isArray(trades) || trades.length === 0) return;
    // avoid mutating input
    const toAdd = trades.map((t) => ({ ...t }));
    this.trades = [...toAdd, ...this.trades];
    this.flush();
    // eslint-disable-next-line no-console
    console.info('[LocalStorageRepo] seeded', toAdd.length, 'trades');
  }

  private flush() {
    try {
      window.localStorage.setItem(this.key, JSON.stringify(this.trades));
      // eslint-disable-next-line no-console
      console.info('[LocalStorageRepo] flushed', this.trades.length, 'trades to localStorage');
      try {
        globalThis.dispatchEvent(new CustomEvent('trades-updated'));
      } catch {
        /* ignore */
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[LocalStorageRepo] failed to persist to localStorage', err);
    }
  }

  private isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private looksLikeVOTrade(obj: unknown): obj is Record<string, unknown> {
    if (!this.isObject(obj)) return false;
    const maybe = obj as Record<string, unknown>;
    return (
      'symbol' in maybe &&
      this.isObject(maybe.symbol) &&
      'value' in (maybe.symbol as Record<string, unknown>)
    );
  }

  private toRepoTrade(obj: unknown): RepoTrade {
    if (this.looksLikeVOTrade(obj)) {
      const o = obj as Record<string, unknown>;
      const symbolVO = o.symbol as Record<string, unknown>;
      const entryDateVO = o.entryDate as Record<string, unknown> | string | undefined;

      return {
        id: String(o.id),
        market: (o.market as RepoTrade['market']) ?? 'All',
        symbol: String(symbolVO.value),
        entryDate:
          entryDateVO && typeof entryDateVO === 'object' && 'value' in entryDateVO
            ? String((entryDateVO as Record<string, unknown>).value)
            : String(entryDateVO ?? new Date().toISOString()),
        confluence: (o.confluence as { timeframe?: string; type: string }[] | undefined) ?? undefined,
        size:
          this.isObject(o.size) && 'value' in (o.size as Record<string, unknown>)
            ? Number((o.size as Record<string, unknown>).value)
            : Number(o.size as number),
        price:
          this.isObject(o.price) && 'value' in (o.price as Record<string, unknown>)
            ? Number((o.price as Record<string, unknown>).value)
            : Number(o.price as number),
        side:
          this.isObject(o.side) && 'value' in (o.side as Record<string, unknown>)
            ? (String((o.side as Record<string, unknown>).value) as 'LONG' | 'SHORT')
            : (o.side as 'LONG' | 'SHORT'),
        status: (o.status as RepoTrade['status']) ?? 'OPEN',
        pnl: Number(o.pnl ?? 0),
        notes: o.notes as string | undefined,
        entry: o.entry as string | undefined,
        sl: typeof o.sl === 'number' ? (o.sl as number) : undefined,
        slIsBE: typeof o.slIsBE === 'boolean' ? (o.slIsBE as boolean) : undefined,
        tp1: typeof o.tp1 === 'number' ? (o.tp1 as number) : undefined,
        tp2: typeof o.tp2 === 'number' ? (o.tp2 as number) : undefined,
        tp3: typeof o.tp3 === 'number' ? (o.tp3 as number) : undefined,
        tp4: typeof o.tp4 === 'number' ? (o.tp4 as number) : undefined,
        tp1IsHit: typeof o.tp1IsHit === 'boolean' ? (o.tp1IsHit as boolean) : undefined,
        tp2IsHit: typeof o.tp2IsHit === 'boolean' ? (o.tp2IsHit as boolean) : undefined,
        tp3IsHit: typeof o.tp3IsHit === 'boolean' ? (o.tp3IsHit as boolean) : undefined,
        tp4IsHit: typeof o.tp4IsHit === 'boolean' ? (o.tp4IsHit as boolean) : undefined,
        margin: typeof o.margin === 'number' ? (o.margin as number) : undefined,
        leverage: typeof o.leverage === 'number' ? (o.leverage as number) : undefined,
        analysisId: typeof o.analysisId === 'string' ? (o.analysisId as string) : undefined,
        userId: typeof o.userId === 'string' ? (o.userId as string) : undefined,
      };
    }

    if (this.isObject(obj)) {
      const o = obj as Record<string, unknown>;
      return {
        id: String(o.id),
        market: (o.market as RepoTrade['market']) ?? 'All',
        symbol: String(o.symbol),
        entryDate: String(o.entryDate),
        confluence: (o.confluence as { timeframe?: string; type: string }[] | undefined) ?? undefined,
        size: Number(o.size as number),
        price: Number(o.price as number),
        side: (o.side as RepoTrade['side']) ?? 'LONG',
        status: (o.status as RepoTrade['status']) ?? 'OPEN',
        pnl: Number(o.pnl ?? 0),
        notes: o.notes as string | undefined,
        entry: o.entry as string | undefined,
        sl: typeof o.sl === 'number' ? (o.sl as number) : undefined,
        slIsBE: typeof o.slIsBE === 'boolean' ? (o.slIsBE as boolean) : undefined,
        tp1: typeof o.tp1 === 'number' ? (o.tp1 as number) : undefined,
        tp2: typeof o.tp2 === 'number' ? (o.tp2 as number) : undefined,
        tp3: typeof o.tp3 === 'number' ? (o.tp3 as number) : undefined,
        tp4: typeof o.tp4 === 'number' ? (o.tp4 as number) : undefined,
        tp1IsHit: typeof o.tp1IsHit === 'boolean' ? (o.tp1IsHit as boolean) : undefined,
        tp2IsHit: typeof o.tp2IsHit === 'boolean' ? (o.tp2IsHit as boolean) : undefined,
        tp3IsHit: typeof o.tp3IsHit === 'boolean' ? (o.tp3IsHit as boolean) : undefined,
        tp4IsHit: typeof o.tp4IsHit === 'boolean' ? (o.tp4IsHit as boolean) : undefined,
        margin: typeof o.margin === 'number' ? (o.margin as number) : undefined,
        leverage: typeof o.leverage === 'number' ? (o.leverage as number) : undefined,
        analysisId: typeof o.analysisId === 'string' ? (o.analysisId as string) : undefined,
        userId: typeof o.userId === 'string' ? (o.userId as string) : undefined,
      };
    }

    const now = new Date().toISOString();
    return {
      id: 'unknown',
      market: 'All',
      symbol: 'UNKNOWN',
      entryDate: now,
      size: 0,
      price: 0,
      side: 'LONG',
      status: 'OPEN',
      pnl: 0,
    };
  }

  // Accept domain Trade entity, persist primitive DTO, and flush
  async save(trade: Trade): Promise<void> {
    const dto = TradeFactory.toDTO(trade);
    const repoTrade = this.toRepoTrade(dto);
    this.trades.push({ ...repoTrade });
    // eslint-disable-next-line no-console
    console.info('[LocalStorageRepo] save', repoTrade.id);
    this.flush();
  }

  // Return domain Trade[] (reconstructed from stored primitives)
  async getAll(): Promise<Trade[]> {
    // ensure we reflect the latest persisted state in localStorage
    try {
      const raw = window.localStorage.getItem(this.key);
      if (raw) {
        const parsed = JSON.parse(raw) as RepoTrade[];
        // update in-memory cache to match persisted state
        this.trades = parsed.map((t) => ({ ...t }));
      }
    } catch {
      /* ignore reload errors and fall back to in-memory cache */
    }

    // convert each RepoTrade -> Trade via TradeFactory
    return this.trades.map((rt) => {
      const input = {
        id: rt.id,
        symbol: rt.symbol,
        entryDate: rt.entryDate,
        size: rt.size,
        price: rt.price,
        side: rt.side,
        status: rt.status,
        notes: rt.notes,
        market: rt.market,
        sl: rt.sl,
        slIsBE: rt.slIsBE,
        userId: rt.userId,
        tp1: rt.tp1,
        tp2: rt.tp2,
        tp3: rt.tp3,
        tp4: rt.tp4,
        tp1IsHit: rt.tp1IsHit,
        tp2IsHit: rt.tp2IsHit,
        tp3IsHit: rt.tp3IsHit,
        tp4IsHit: rt.tp4IsHit,
        leverage: rt.leverage,
        margin: rt.margin,
        analysisId: rt.analysisId,
      };
      try {
        return TradeFactory.create(input);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[LocalStorageRepo] failed to convert stored trade to domain Trade', err);
        // create a minimal Trade to keep API stable
        return TradeFactory.create({
          id: rt.id,
          symbol: rt.symbol,
          entryDate: rt.entryDate,
          size: rt.size,
          price: rt.price,
          side: rt.side,
        });
      }
    });
  }

  async update(trade: Trade): Promise<void> {
    const dto = TradeFactory.toDTO(trade);
    const repoTrade = this.toRepoTrade(dto);
    const idx = this.trades.findIndex((t) => t.id === repoTrade.id);
    if (idx >= 0) {
      this.trades[idx] = { ...this.trades[idx], ...repoTrade };
      // eslint-disable-next-line no-console
      console.info('[LocalStorageRepo] update', repoTrade.id);
      this.flush();
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        '[LocalStorageRepo] update: trade not found, performing save instead',
        repoTrade.id
      );
      await this.save(trade);
    }
  }

  // Delete a trade by id
  async delete(id: string): Promise<void> {
    const idx = this.trades.findIndex((t) => t.id === id);
    if (idx >= 0) {
      this.trades.splice(idx, 1);
      // eslint-disable-next-line no-console
      console.info('[LocalStorageRepo] delete', id);
      this.flush();
    }
  }
}

export default LocalStorageTradeRepository;
